import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Camera, CheckCircle2, HandHeart, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { DateField } from "@/components/common/DateField";
import { api, apiError } from "@/lib/api";

const SCOPE_LABEL = {
  website: "On our own website, including the Wall of Community Heroes",
  print: "In printed material — newsletters, annual report, posters",
  social: "On our social media accounts",
};

const BLANK = {
  phone: "", address: "", date_of_birth: "", education: "", family_background: "",
  occupation: "", skills: "", languages: "", availability: "", bio: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  consent_granted: false, consent_scopes: [], information_confirmed: false, password: "",
};

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11.5px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] text-muted-foreground">{hint}</p>}
  </div>
);

export default function VolunteerApply() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/public/volunteer-invite/${token}`);
      setInvite(res.data);
    } catch (e) {
      setError(apiError(e));
      setInvite(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleScope = (scope) => setForm((f) => ({
    ...f,
    consent_scopes: f.consent_scopes.includes(scope)
      ? f.consent_scopes.filter((s) => s !== scope)
      : [...f.consent_scopes, scope],
  }));

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/public/volunteer-invite/${token}/submit`, {
        ...form,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
        consent_scopes: form.consent_granted ? form.consent_scopes : [],
        photo_token: photo?.photo_token || "",
      });
      setDone(res.data);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const res = await api.post(`/public/volunteer-invite/${token}/photo`, body,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto({ ...res.data, preview: URL.createObjectURL(file) });
      toast.success("Photograph uploaded");
    } catch (e) { toast.error(apiError(e)); }
    setUploading(false);
  };

  if (invite === null) {
    return <Shell><div className="flex items-center gap-2 text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Opening your invitation…</div></Shell>;
  }

  if (invite === false) {
    return (
      <Shell>
        <div className="panel max-w-md p-7 text-center" data-testid="volunteer-apply-error">
          <h1 className="font-display text-xl font-bold text-foreground">
            This invitation cannot be opened
          </h1>
          <p className="mt-2 text-[13px] text-muted-foreground">{error}</p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="panel max-w-md p-7 text-center" data-testid="volunteer-apply-done">
          <CheckCircle2 className="mx-auto h-9 w-9 text-emerald-500" />
          <h1 className="mt-3 font-display text-xl font-bold text-foreground">Thank you</h1>
          <p className="mt-2 text-[13px] text-muted-foreground">{done.message}</p>
          <p className="mt-3 font-mono text-[11px] text-muted-foreground"
             data-testid="volunteer-apply-ref">{done.application_ref}</p>
        </div>
      </Shell>
    );
  }

  const ready = form.information_confirmed && form.password.length >= 8
    && (!form.consent_granted || form.consent_scopes.length > 0);

  return (
    <Shell>
      <div className="w-full max-w-2xl space-y-5" data-testid="volunteer-apply-form">
        <div className="panel p-6">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-[10px] bg-gradient-to-br from-primary to-[hsl(var(--teal))]">
              <HandHeart className="h-4 w-4 text-white" />
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-[1.6px] text-muted-foreground">
                Volunteer registration · {invite.invite_ref}
              </div>
              <h1 className="font-display text-xl font-bold text-foreground">
                Welcome, {invite.name}
              </h1>
            </div>
          </div>
          <p className="mt-3 text-[13px] text-muted-foreground">
            Complete your details below and choose a password. Your account opens once our team has
            approved it. Your registered email address is{" "}
            <span className="font-mono text-foreground">{invite.email}</span>.
          </p>
        </div>

        <div className="panel space-y-4 p-6">
          <h2 className="text-base font-semibold text-foreground md:text-lg">About you</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Telephone">
              <Input value={form.phone} data-testid="apply-phone"
                     onChange={(e) => set("phone")(e.target.value)} />
            </Field>
            <Field label="Date of birth">
              <DateField value={form.date_of_birth} testId="apply-dob"
                         onChange={(v) => set("date_of_birth")(v || "")} />
            </Field>
            <Field label="Address">
              <Input value={form.address} data-testid="apply-address"
                     onChange={(e) => set("address")(e.target.value)} />
            </Field>
            <Field label="Occupation">
              <Input value={form.occupation} data-testid="apply-occupation"
                     onChange={(e) => set("occupation")(e.target.value)} />
            </Field>
          </div>
          <Field label="Education" hint="Qualifications, institutions and years.">
            <Textarea value={form.education} rows={3} data-testid="apply-education"
                      onChange={(e) => set("education")(e.target.value)} />
          </Field>
          <Field label="Family background"
                 hint="Only what you are comfortable sharing. It helps us place you well.">
            <Textarea value={form.family_background} rows={3} data-testid="apply-family"
                      onChange={(e) => set("family_background")(e.target.value)} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Skills" hint="Separate with commas.">
              <Input value={form.skills} data-testid="apply-skills"
                     onChange={(e) => set("skills")(e.target.value)} />
            </Field>
            <Field label="Languages" hint="Separate with commas.">
              <Input value={form.languages} data-testid="apply-languages"
                     onChange={(e) => set("languages")(e.target.value)} />
            </Field>
            <Field label="When you are usually free">
              <Input value={form.availability} data-testid="apply-availability"
                     placeholder="Weekends, weekday evenings…"
                     onChange={(e) => set("availability")(e.target.value)} />
            </Field>
            <Field label="A short introduction">
              <Input value={form.bio} data-testid="apply-bio"
                     onChange={(e) => set("bio")(e.target.value)} />
            </Field>
            <Field label="Emergency contact name">
              <Input value={form.emergency_contact_name} data-testid="apply-emergency-name"
                     onChange={(e) => set("emergency_contact_name")(e.target.value)} />
            </Field>
            <Field label="Emergency contact telephone">
              <Input value={form.emergency_contact_phone} data-testid="apply-emergency-phone"
                     onChange={(e) => set("emergency_contact_phone")(e.target.value)} />
            </Field>
          </div>
        </div>

        <div className="panel space-y-3 p-6">
          <h2 className="text-base font-semibold text-foreground md:text-lg">
            Photographs and your story
          </h2>
          <p className="text-[12.5px] text-muted-foreground">
            We will never publish your photograph, name or story without your permission, and you can
            withdraw it at any time from your volunteer account.
          </p>
          <div className="flex items-center gap-4">
            {photo?.preview
              ? <img src={photo.preview} alt="" data-testid="apply-photo-preview"
                     className="h-20 w-20 rounded-xl object-cover" />
              : <span className="grid h-20 w-20 place-items-center rounded-xl bg-muted text-[11px] text-muted-foreground">
                  No photo
                </span>}
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-3.5 py-2 text-[12px] text-muted-foreground transition-colors duration-150 hover:text-foreground">
              <input type="file" accept="image/*" className="hidden" data-testid="apply-photo-input"
                     onChange={(e) => uploadPhoto(e.target.files?.[0])} />
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Camera className="h-3.5 w-3.5" />}
              {photo ? "Choose a different photograph" : "Upload my photograph"}
            </label>
          </div>
          <label className="flex items-start gap-2.5 rounded-xl border border-border bg-muted/25 p-3">
            <Checkbox checked={form.consent_granted} data-testid="apply-consent-granted"
                      onCheckedChange={(v) => set("consent_granted")(Boolean(v))} />
            <span className="text-[12.5px] text-foreground">
              I give permission for my name, photograph and story to be used.
            </span>
          </label>
          {form.consent_granted && (
            <div className="space-y-2 pl-1" data-testid="apply-consent-scopes">
              {(invite.consent_scopes || []).map((scope) => (
                <label key={scope} className="flex items-start gap-2.5">
                  <Checkbox checked={form.consent_scopes.includes(scope)}
                            data-testid={`apply-consent-${scope}`}
                            onCheckedChange={() => toggleScope(scope)} />
                  <span className="text-[12.5px] text-muted-foreground">
                    {SCOPE_LABEL[scope] || scope}
                  </span>
                </label>
              ))}
              {form.consent_scopes.length === 0 && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400">
                  Choose at least one place, or leave permission unticked.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="panel space-y-4 p-6">
          <h2 className="text-base font-semibold text-foreground md:text-lg">Your account</h2>
          <Field label="Choose a password (8 characters or more)">
            <Input type="password" value={form.password} autoComplete="new-password"
                   data-testid="apply-password"
                   onChange={(e) => set("password")(e.target.value)} />
          </Field>
          <label className="flex items-start gap-2.5">
            <Checkbox checked={form.information_confirmed} data-testid="apply-confirm"
                      onCheckedChange={(v) => set("information_confirmed")(Boolean(v))} />
            <span className="text-[12.5px] text-foreground">
              I confirm that everything I have entered is correct.
            </span>
          </label>
          <Button onClick={submit} disabled={busy || !ready} data-testid="apply-submit"
                  className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <ShieldCheck className="mr-2 h-4 w-4" />} Send my registration
          </Button>
        </div>
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="grid min-h-screen place-items-center bg-background px-4 py-10">{children}</div>
);
